import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  View,
  Text,
  ScrollView,
  SafeAreaView,
  TextInput,
  TouchableOpacity,
  useWindowDimensions,
} from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import { useCssStyle } from '../../customHook/getCssStyle';
import { setRenderFlashMessage } from '../../Redux/Actions/appConfigAction';
import CustomPressable from '../../components/customPressable/customPressable';
import { ImageComp } from '../../components/Image/Image';
import { CHEVRON_DOWN, CHEVRON_UP, CROSS, MINUS, PLUS } from '../../assets';
import CustomCheckBoxWithProps from '../../components/checkBox/customCheckBox';
import { CustomModal } from '../../components/customModal';
import RenderHTML from 'react-native-render-html';
import { getCleanUrl } from '../../utils/Helper';

// Maximum nesting depth for addon groups (protects against deeply nested / circular data)
const MAX_NESTED_DEPTH = 10;

// Pure helper — computes the full addon list to send (leaf + all ancestors) for any
// given selected list. Extracted so toggleAddon can preview a proposed selection
// before committing to state.
const computeAllAddonsToSend = (selectedAddons, parentMap) => {
  const result = [];
  const addedIds = new Set();
  for (const selected of selectedAddons) {
    if (!addedIds.has(String(selected.id))) {
      result.push(selected);
      addedIds.add(String(selected.id));
    }
    const visitedGroups = new Set();
    let currentGroupId = String(selected.addOnGroupId);
    while (parentMap[currentGroupId] && !visitedGroups.has(currentGroupId)) {
      visitedGroups.add(currentGroupId);
      const { addon, addonGroupId } = parentMap[currentGroupId];
      if (!addedIds.has(String(addon.id))) {
        result.push({ ...addon, addOnGroupId: addonGroupId, quantity: 1 });
        addedIds.add(String(addon.id));
      }
      currentGroupId = String(addonGroupId);
    }
  }
  return result;
};

const ItemGroupedAddonsModal = (props) => {
  const {
    selectedStoreItem = {},
    addonsList = {},
    addOnGroupList = {},
    configs = {},
    handleAddToCart,
    handleClose,
  } = props || {};

  const getStyles = useCssStyle();
  const dispatch = useDispatch();
  const { width, height } = useWindowDimensions();

  const { currencyDetails } = useSelector((state) => state.deviceConfigSlice);
  const currencyCode = currencyDetails?.code || '';

  const [itemAddOnsInfoGroup, setItemAddOnsInfoGroup] = useState([]);
  const [localSelectedAddon, setLocalSelectedAddon] = useState([]);
  // Required groups start open, optional groups start closed
  const [collapsedGroups, setCollapsedGroups] = useState(new Set());
  const [expandedNestedAddons, setExpandedNestedAddons] = useState({});
  const [cookingInstructions, setCookingInstructions] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [showFullDesc, setShowFullDesc] = useState(false);

  const getNestedGroupsForAddon = useCallback((addon) => {
    const refs = addon.addonGroupReferences || addon.addOnGroupReferences || [];
    if (refs.length === 0) return [];
    return refs
      .map(groupId => {
        const group = addOnGroupList[groupId] || addOnGroupList[String(groupId)];
        if (!group) return null;
        const addonRefs = group.addonReferences || group.addOnReferences || [];
        const groupAddons = addonRefs
          .map(id => {
            const a = addonsList[id] || addonsList[String(id)];
            if (!a) return null;
            return {
              ...a,
              groupId: group.id,
              groupName: group.addOnGroupName,
              hasNestedGroups:
                (a.addonGroupReferences?.length > 0) || (a.addOnGroupReferences?.length > 0),
            };
          })
          .filter(Boolean)
          .sort((a, b) => (a.priority || 0) - (b.priority || 0));
        return {
          id: group.id,
          addOnGroupName: group.addOnGroupName,
          addOnGroupDescription: group.addOnGroupDescription,
          min: group.minAddOnLimit || 0,
          max: group.maxAddOnLimit || 999,
          addons: groupAddons,
        };
      })
      .filter(Boolean)
      .sort((a, b) => (a.priority || 0) - (b.priority || 0));
  }, [addonsList, addOnGroupList]);

  useEffect(() => {
    try {
      if (!addonsList || Object.keys(addonsList).length === 0) return;
      const groupRefs = selectedStoreItem.addOnGroupReferences || [];
      if (groupRefs.length === 0) return;

      const groups = groupRefs
        .map(groupId => {
          const group = addOnGroupList[groupId] || addOnGroupList[String(groupId)];
          if (!group) return null;
          const addonRefs = group.addonReferences || group.addOnReferences || [];
          const groupAddons = addonRefs
            .map(id => {
              const a = addonsList[id] || addonsList[String(id)];
              if (!a) return null;
              return {
                ...a,
                groupId: group.id,
                groupName: group.addOnGroupName,
                hasNestedGroups:
                  (a.addonGroupReferences?.length > 0) || (a.addOnGroupReferences?.length > 0),
              };
            })
            .filter(Boolean)
            .sort((a, b) => (a.priority || 0) - (b.priority || 0));
          if (groupAddons.length === 0) return null;
          return {
            id: group.id,
            addOnGroupName: group.addOnGroupName,
            addOnGroupDescription: group.addOnGroupDescription,
            min: group.minAddOnLimit || 0,
            max: group.maxAddOnLimit || 999,
            priority: group.priority || 1,
            addons: groupAddons,
          };
        })
        .filter(Boolean)
        .sort((a, b) => (a.priority || 0) - (b.priority || 0));

      setItemAddOnsInfoGroup(groups);
      // Required groups (min > 0) start open; optional groups start collapsed
      setCollapsedGroups(new Set(groups.filter(g => g.min === 0).map(g => String(g.id))));
    } catch (e) {
      console.error('ItemGroupedAddonsModal error:', e);
    }
  }, [addonsList, addOnGroupList, selectedStoreItem]);

  const toggleGroupCollapse = useCallback((groupId) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      const key = String(groupId);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  }, []);

  const toggleNestedDropdown = useCallback((addonId) => {
    setExpandedNestedAddons(prev => ({ ...prev, [String(addonId)]: !prev[String(addonId)] }));
  }, []);

  // Build a reverse map: nestedGroupId -> { addon (parent), addonGroupId (parent's own group) }
  // Traverses the full addon tree at any depth so we can walk the entire ancestor chain.
  const parentMap = useMemo(() => {
    const map = {};
    const visited = new Set();

    const searchInGroup = (groupId, addons) => {
      if (visited.has(String(groupId))) return;
      visited.add(String(groupId));

      for (const addon of addons) {
        if (!addon.hasNestedGroups) continue;
        const nestedRefs = addon.addonGroupReferences || addon.addOnGroupReferences || [];
        for (const ngId of nestedRefs) {
          const key = String(ngId);
          if (!map[key]) {
            map[key] = { addon, addonGroupId: groupId };
          }
          // Recurse into this nested group to map its addons' nested groups too
          const nestedGroup = addOnGroupList[ngId] || addOnGroupList[key];
          if (nestedGroup) {
            const nestedAddonRefs = nestedGroup.addonReferences || nestedGroup.addOnReferences || [];
            const nestedAddons = nestedAddonRefs
              .map(id => {
                const a = addonsList[id] || addonsList[String(id)];
                if (!a) return null;
                // Compute hasNestedGroups since raw addonsList entries don't have it
                return {
                  ...a,
                  hasNestedGroups: (a.addonGroupReferences?.length > 0) || (a.addOnGroupReferences?.length > 0),
                };
              })
              .filter(Boolean);
            searchInGroup(ngId, nestedAddons);
          }
        }
      }
    };

    for (const group of itemAddOnsInfoGroup) {
      searchInGroup(group.id, group.addons);
    }

    return map;
  }, [itemAddOnsInfoGroup, addonsList, addOnGroupList]);

  const allAddonsToSend = useMemo(
    () => computeAllAddonsToSend(localSelectedAddon, parentMap),
    [localSelectedAddon, parentMap],
  );

  const toggleAddon = useCallback((addon, group) => {
    setLocalSelectedAddon(prev => {
      const updated = [...prev];
      const idx = updated.findIndex(a => String(a.id) === String(addon.id));
      if (idx !== -1) {
        updated.splice(idx, 1);
      } else {
        // Preview the full expanded addon list (leaf + all auto-included ancestors)
        // if this addon were added, then validate every affected group's max limit.
        const proposed = [...updated, { ...addon, addOnGroupId: group.id, quantity: 1 }];
        const proposedAll = computeAllAddonsToSend(proposed, parentMap);

        // Collect counts per group in the proposed list
        const countPerGroup = {};
        for (const a of proposedAll) {
          const gId = String(a.addOnGroupId);
          countPerGroup[gId] = (countPerGroup[gId] || 0) + 1;
        }

        // Check every affected group against its maxAddOnLimit
        for (const [gId, count] of Object.entries(countPerGroup)) {
          const topGroup = itemAddOnsInfoGroup.find(g => String(g.id) === gId);
          const rawGroup = addOnGroupList[gId] || addOnGroupList[Number(gId)];
          const groupDef = topGroup || rawGroup;
          if (!groupDef) continue;
          // processed groups use `max`, raw groups use `maxAddOnLimit`; 0 means unlimited
          const max = groupDef.max || groupDef.maxAddOnLimit || 0;
          if (max > 0 && count > max) {
            const name = groupDef.addOnGroupName;
            dispatch(setRenderFlashMessage(true, {
              type: 'danger',
              message: `Max ${max} selection${max !== 1 ? 's' : ''} allowed for ${name}`,
            }));
            return prev;
          }
        }

        updated.push({ ...addon, addOnGroupId: group.id, quantity: 1 });
      }
      return updated;
    });
  }, [dispatch, parentMap, itemAddOnsInfoGroup, addOnGroupList]);

  // Maps parent addon ID → array of selected leaf addon names under it.
  // Used to show a "Name1, Name2" summary on collapsed expandable addon rows.
  const activeParentSelections = useMemo(() => {
    const map = {};
    for (const selected of localSelectedAddon) {
      const visitedGroups = new Set();
      let currentGroupId = String(selected.addOnGroupId);
      while (parentMap[currentGroupId] && !visitedGroups.has(currentGroupId)) {
        visitedGroups.add(currentGroupId);
        const { addon, addonGroupId } = parentMap[currentGroupId];
        const parentId = String(addon.id);
        if (!map[parentId]) map[parentId] = [];
        if (!map[parentId].includes(selected.name)) map[parentId].push(selected.name);
        currentGroupId = String(addonGroupId);
      }
    }
    return map;
  }, [localSelectedAddon, parentMap]);

  const totalPrice = useMemo(() => {
    const addonTotal = allAddonsToSend.reduce((s, a) => s + parseFloat(a.price || 0), 0);
    return ((parseFloat(selectedStoreItem.itemPrice || 0) + addonTotal) * quantity).toFixed(2);
  }, [allAddonsToSend, selectedStoreItem.itemPrice, quantity]);

  const addToOrder = useCallback(() => {
    // Validate using allAddonsToSend so parent addons (auto-included) count towards
    // their group's minimum requirement
    const groupCounts = {};
    for (const a of allAddonsToSend) {
      const gId = String(a.addOnGroupId);
      groupCounts[gId] = (groupCounts[gId] || 0) + 1;
    }
    for (const group of itemAddOnsInfoGroup) {
      const selected = groupCounts[String(group.id)] || 0;
      if (selected < group.min) {
        dispatch(setRenderFlashMessage(true, {
          type: 'danger',
          message: `Please select at least ${group.min} option${group.min !== 1 ? 's' : ''} for ${group.addOnGroupName}`,
        }));
        return;
      }
    }
    const itemPayload = cookingInstructions
      ? { ...selectedStoreItem, quantity, cookingInstructions }
      : { ...selectedStoreItem, quantity };
    handleAddToCart(itemPayload, allAddonsToSend, true);
    handleClose();
  }, [
    allAddonsToSend, itemAddOnsInfoGroup, dispatch, selectedStoreItem,
    quantity, cookingInstructions, handleAddToCart, handleClose,
  ]);

  const descriptionStyle = useMemo(() => {
    const s = getStyles(['list_itemContainer_textContainer_subtitle']);
    return Array.isArray(s) ? Object.assign({}, ...s) : s || {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderAddonRow = (addon, group, depth = 0, visitedIds = new Set()) => {
    // Guard against circular references and excessive depth
    if (depth > MAX_NESTED_DEPTH || visitedIds.has(String(addon.id))) return null;
    const nextVisited = new Set([...visitedIds, String(addon.id)]);

    const isSelected = localSelectedAddon.some(a => String(a.id) === String(addon.id));
    const isExpanded = !!expandedNestedAddons[String(addon.id)];
    const hasPrice = parseFloat(addon.price || 0) > 0;
    const childSelections = activeParentSelections[String(addon.id)];

    if (addon.hasNestedGroups) {
      const nestedGroups = isExpanded ? getNestedGroupsForAddon(addon) : [];
      return (
        <View key={addon.id}>
          <TouchableOpacity
            style={[getStyles(['hcommerce_grouped_addonRow']), depth > 0 && getStyles(['hcommerce_grouped_nestedAddonRow'])]}
            onPress={() => toggleNestedDropdown(addon.id)}
            activeOpacity={0.7}
          >
            <View style={getStyles(['hcommerce_grouped_addonText'])}>
              <Text style={getStyles(['list_itemContainer_textContainer_title'])} numberOfLines={2}>
                {addon.name}
              </Text>
              {hasPrice && (
                <Text style={getStyles(['AddOnsModalContent_addons_itemContainer_textContainer_price'])}>
                  +{currencyCode}{addon.price}
                </Text>
              )}
              {childSelections?.length > 0 && (
                <Text style={getStyles(['hcommerce_grouped_parentSelectionHint'])} numberOfLines={1}>
                  {childSelections.join(', ')}
                </Text>
              )}
            </View>
            <ImageComp
              source={isExpanded ? CHEVRON_UP : CHEVRON_DOWN}
              style={getStyles(['AddOnGrouping_drawer_icon'])}
            />
          </TouchableOpacity>

          {isExpanded && (
            <View style={getStyles(['hcommerce_grouped_nestedContainer'])}>
              {nestedGroups.length > 0 ? nestedGroups.map(ng => (
                <View key={ng.id} style={getStyles(['hcommerce_grouped_nestedGroupBlock'])}>
                  <View style={getStyles(['hcommerce_grouped_nestedGroupHeaderRow'])}>
                    <Text style={[getStyles(['list_itemContainer_textContainer_title']), getStyles(['hcommerce_grouped_nestedGroupName'])]}>
                      {ng.addOnGroupName}
                    </Text>
                    <View style={[getStyles(['hcommerce_grouped_badge']), ng.min > 0 ? getStyles(['hcommerce_grouped_requiredBadge']) : getStyles(['hcommerce_grouped_optionalBadge'])]}>
                      <Text style={[getStyles(['hcommerce_grouped_badgeText']), ng.min > 0 ? getStyles(['hcommerce_grouped_requiredBadgeText']) : getStyles(['hcommerce_grouped_optionalBadgeText'])]}>
                        {ng.min > 0 ? 'Required' : 'Optional'}
                      </Text>
                    </View>
                  </View>
                  {ng.addOnGroupDescription ? (
                    <Text style={[getStyles(['list_itemContainer_textContainer_subtitle']), getStyles(['hcommerce_grouped_nestedGroupDesc'])]}>
                      {ng.addOnGroupDescription}
                    </Text>
                  ) : null}
                  {ng.addons.map(a => renderAddonRow(a, ng, depth + 1, nextVisited))}
                </View>
              )) : (
                <Text style={[getStyles(['list_itemContainer_textContainer_subtitle']), getStyles(['hcommerce_grouped_emptyText'])]}>
                  No options available
                </Text>
              )}
            </View>
          )}
        </View>
      );
    }

    return (
      <TouchableOpacity
        key={addon.id}
        style={[getStyles(['hcommerce_grouped_addonRow']), depth > 0 && getStyles(['hcommerce_grouped_nestedAddonRow'])]}
        onPress={() => toggleAddon(addon, group)}
        activeOpacity={0.8}
      >
        <View style={getStyles(['hcommerce_grouped_addonRowLeft'])}>
          <View style={getStyles(['hcommerce_grouped_addonText'])}>
            <Text style={getStyles(['list_itemContainer_textContainer_title'])} numberOfLines={2}>
              {addon.name}
            </Text>
            {addon.description ? (
              <Text style={getStyles(['list_itemContainer_textContainer_subtitle'])} numberOfLines={1}>
                {addon.description}
              </Text>
            ) : null}
            {hasPrice && (
              <Text style={getStyles(['AddOnsModalContent_addons_itemContainer_textContainer_price'])}>
                +{currencyCode}{addon.price}
              </Text>
            )}
          </View>
        </View>
        <CustomCheckBoxWithProps
          value={isSelected}
          onValueChange={() => toggleAddon(addon, group)}
        />
      </TouchableOpacity>
    );
  };

  const itemImage = selectedStoreItem.storeItemImages?.find(i => i.imageType === 'LARGE')?.imagePath
    || selectedStoreItem.storeItemImages?.[0]?.imagePath;
  const cleanImageUrl = itemImage ? getCleanUrl(itemImage) : null;

  return (
    <CustomModal onRequestClose={handleClose} landscapeStyle={getStyles(["hcommerce_modal_landscape"])}>
      {/* Close button above the sheet, centered on backdrop */}
      <View style={getStyles(['hcommerce_modal_closeAboveContainer'])}>
        <CustomPressable onPress={handleClose} style={getStyles(['hcommerce_modal_closeAboveBtn'])}>
          <ImageComp source={CROSS} style={getStyles(['hcommerce_modal_closeAboveIcon'])} />
        </CustomPressable>
      </View>

      <View style={[getStyles(['hcommerce_modal_sheet']), { maxHeight: height * 0.92 }]}>

        {/* Drag indicator */}
        <View style={getStyles(['hcommerce_modal_dragIndicatorWrap'])}>
          <View style={getStyles(['hcommerce_modal_dragIndicator'])} />
        </View>

        {/* Scrollable content */}
        <ScrollView
          style={getStyles(['hcommerce_modal_scroll'])}
          contentContainerStyle={getStyles(['hcommerce_modal_scrollContent'])}
          showsVerticalScrollIndicator={false}
        >
          {cleanImageUrl && (
            <View style={getStyles(['hcommerce_modal_imageContainer'])}>
              <ImageComp source={cleanImageUrl} style={getStyles(['hcommerce_modal_image'])} resizeMode="cover" />
            </View>
          )}

          <View style={getStyles(['hcommerce_modal_itemInfo'])}>
            <Text style={[getStyles(['AddOnsModalContent_childcontainer_header_container_title']), getStyles(['hcommerce_modal_itemName'])]}>
              {selectedStoreItem.itemName}
            </Text>
            <Text style={[getStyles(['AddOnsModalContent_childcontainer_header_container_subtitle']), getStyles(['hcommerce_modal_itemPrice'])]}>
              {currencyCode}{selectedStoreItem.itemPrice}
            </Text>
            {selectedStoreItem.itemDescription ? (
              <>
                <View style={showFullDesc ? null : getStyles(['hcommerce_modal_descClamp'])}>
                  <RenderHTML
                    contentWidth={width - 48}
                    source={{ html: selectedStoreItem.itemDescription }}
                    baseStyle={{ ...descriptionStyle, textAlign: 'center' }}
                  />
                </View>
                <TouchableOpacity onPress={() => setShowFullDesc(v => !v)} activeOpacity={0.7}>
                  <Text style={getStyles(['hcommerce_modal_showMoreBtn'])}>
                    {showFullDesc ? 'Show less' : 'Show more'}
                  </Text>
                </TouchableOpacity>
              </>
            ) : null}
          </View>

          {/* Addon groups — card style, required open, optional closed */}
          {itemAddOnsInfoGroup.map(group => {
            const isCollapsed = collapsedGroups.has(String(group.id));
            const selectedCount = allAddonsToSend.filter(
              a => String(a.addOnGroupId) === String(group.id)
            ).length;
            const isRequired = group.min > 0;

            return (
              <View key={group.id} style={[getStyles(['hcommerce_grouped_groupCard']), isRequired && getStyles(['hcommerce_grouped_groupCardRequired'])]}>
                {/* Group header — always tappable to collapse/expand */}
                <TouchableOpacity
                  style={getStyles(['hcommerce_grouped_groupHeader'])}
                  onPress={() => toggleGroupCollapse(group.id)}
                  activeOpacity={0.7}
                >
                  <View style={getStyles(['hcommerce_grouped_groupHeaderText'])}>
                    <View style={getStyles(['hcommerce_grouped_groupTitleRow'])}>
                      <Text style={getStyles(['AddOnGrouping_drawer_header_title'])}>
                        {group.addOnGroupName}
                      </Text>
                      <View style={[getStyles(['hcommerce_grouped_badge']), isRequired ? getStyles(['hcommerce_grouped_requiredBadge']) : getStyles(['hcommerce_grouped_optionalBadge'])]}>
                        <Text style={[getStyles(['hcommerce_grouped_badgeText']), isRequired ? getStyles(['hcommerce_grouped_requiredBadgeText']) : getStyles(['hcommerce_grouped_optionalBadgeText'])]}>
                          {isRequired ? 'Required' : 'Optional'}
                        </Text>
                      </View>
                    </View>
                    {(selectedCount > 0 || isRequired || isCollapsed) ? (
                      <Text style={getStyles(['AddOnGrouping_drawer_header_subtitle'])}>
                        {selectedCount > 0
                          ? `${selectedCount} selected`
                          : isRequired
                            ? `Choose ${group.max === 1 ? '1' : `up to ${group.max}`}`
                            : 'Tap to expand'}
                      </Text>
                    ) : null}
                    {group.addOnGroupDescription ? (
                      <Text style={getStyles(['list_itemContainer_textContainer_subtitle'])}>
                        {group.addOnGroupDescription}
                      </Text>
                    ) : null}
                  </View>
                  <ImageComp
                    source={isCollapsed ? CHEVRON_DOWN : CHEVRON_UP}
                    style={getStyles(['AddOnGrouping_drawer_icon'])}
                  />
                </TouchableOpacity>

                {/* Addon list — shown when expanded */}
                {!isCollapsed && (
                  <>
                    <View style={getStyles(['hcommerce_grouped_groupDivider'])} />
                    <View style={getStyles(['hcommerce_grouped_groupContent'])}>
                      {group.addons.map(addon => renderAddonRow(addon, group, 0))}
                    </View>
                  </>
                )}
              </View>
            );
          })}

          {/* Cooking instructions */}
          <View style={getStyles(['hcommerce_grouped_instructionsSection'])}>
            <Text style={getStyles(['AddOnGrouping_drawer_header_title'])}>Special Instructions</Text>
            <Text style={getStyles(['AddOnGrouping_drawer_header_subtitle'])}>Optional</Text>
            <TextInput
              style={getStyles(['hcommerce_modal_instructionsInput'])}
              placeholder="e.g. No onions, extra sauce..."
              placeholderTextColor="rgba(0,0,0,0.4)"
              value={cookingInstructions}
              onChangeText={setCookingInstructions}
              multiline
              numberOfLines={3}
              textAlignVertical="top"
            />
          </View>

          <View style={getStyles(['hcommerce_modal_bottomPad'])} />
        </ScrollView>

        {/* Footer: quantity + add button inline */}
        <SafeAreaView edges={['bottom']}>
          <View style={getStyles(['hcommerce_modal_footer'])}>
            {!configs.disableAddRemoveItemsButtons && (
              <View style={getStyles(['hcommerce_modal_footerRow'])}>
                <View style={getStyles(['hcommerce_modal_qtyBlock'])}>
                  <CustomPressable style={getStyles(['hcommerce_modal_qtyBtn'])} onPress={() => setQuantity(q => Math.max(1, q - 1))}>
                    <ImageComp source={MINUS} style={getStyles(['hcommerce_modal_qtyIcon'])} />
                  </CustomPressable>
                  <Text style={getStyles(['additem_controls_qty_text'])}>{quantity}</Text>
                  <CustomPressable style={getStyles(['hcommerce_modal_qtyBtn'])} onPress={() => setQuantity(q => q + 1)}>
                    <ImageComp source={PLUS} style={getStyles(['hcommerce_modal_qtyIcon'])} />
                  </CustomPressable>
                </View>
                <CustomPressable
                  style={[getStyles(['primaryBtn']), getStyles(['hcommerce_modal_addBtnFlex'])]}
                  onPress={addToOrder}
                  id="primaryBtn"
                >
                  <Text style={getStyles(['primaryBtnTxt'])}>
                    Add to Order · {currencyCode}{totalPrice}
                  </Text>
                </CustomPressable>
              </View>
            )}
          </View>
        </SafeAreaView>
      </View>
    </CustomModal>
  );
};

export default ItemGroupedAddonsModal;
